import JSZip from "jszip";
import * as XLSX from "xlsx";
import { BlockType } from "../components/Chat/Chat.types";
import type { UIMessage } from "../components/Chat/Chat.types";
import { chatApi, type WorkbookArtifactItem } from "../services/chatApi";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ParsedCell {
	value: string | number;
	format?: string;
}

export interface ParsedTable {
	columns: { key: string; label: string }[];
	rows: Record<string, string>[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function decimalPlaces(s: string): number {
	const dot = s.indexOf(".");
	return dot === -1 ? 0 : s.length - dot - 1;
}

// ---------------------------------------------------------------------------
// parseNumericCell
// ---------------------------------------------------------------------------

/**
 * Coerce a raw display string into a typed value suitable for XLSX export.
 *
 * Handles currency (`$1,234.56`), percentages (`45.2%`), multipliers (`3.5x`),
 * and parenthetical negatives (`(1,234)`).  Non-numeric cells fall back to the
 * original string so labels, dashes, and "N/A" pass through unchanged.
 */
export function parseNumericCell(raw: string): ParsedCell {
	const trimmed = raw.trim();

	if (!trimmed || trimmed === "-" || /^n\/?a$/i.test(trimmed)) {
		return { value: trimmed };
	}

	let work = trimmed;
	let negative = false;
	let dollar = false;
	let percent = false;
	let multiplier = false;

	// Parenthetical negatives: (123), ($1,234.56)
	const parenMatch = work.match(/^\((.+)\)$/);
	if (parenMatch) {
		work = parenMatch[1].trim();
		negative = true;
	}

	// Leading minus — may precede or follow $
	if (work.startsWith("-")) {
		negative = true;
		work = work.slice(1);
	}

	if (work.startsWith("$")) {
		dollar = true;
		work = work.slice(1);
	}

	// Minus after $: $-1,234
	if (work.startsWith("-")) {
		negative = true;
		work = work.slice(1);
	}

	if (work.endsWith("%")) {
		percent = true;
		work = work.slice(0, -1);
	} else if (work.endsWith("x")) {
		multiplier = true;
		work = work.slice(0, -1);
	}

	work = work.replace(/,/g, "").trim();

	if (!/^\d+(\.\d+)?$/.test(work)) {
		return { value: trimmed };
	}

	const num = Number(work);
	if (!isFinite(num)) {
		return { value: trimmed };
	}

	let value: number = negative ? -num : num;
	const dp = decimalPlaces(work);

	if (percent) {
		value /= 100;
		const fmt = dp > 0 ? `0.${"0".repeat(dp)}%` : "0%";
		return { value, format: fmt };
	}

	if (dollar) {
		const fmt = dp > 0 ? `$#,##0.${"0".repeat(dp)}` : "$#,##0";
		return { value, format: fmt };
	}

	if (multiplier) {
		const fmt = dp > 0 ? `#,##0.${"0".repeat(dp)}"x"` : '#,##0"x"';
		return { value, format: fmt };
	}

	return { value };
}

// ---------------------------------------------------------------------------
// containerHasTables
// ---------------------------------------------------------------------------

/**
 * Quick predicate used to conditionally render the Excel export button.
 * Scoped to `<table>` elements inside the given container (the message
 * content ref, never the thinking dropdown).
 */
export function containerHasTables(container: HTMLElement | null): boolean {
	return !!container && container.querySelectorAll("table").length > 0;
}

// ---------------------------------------------------------------------------
// DOM table extraction
// ---------------------------------------------------------------------------

interface ExtractedTable {
	headers: string[];
	/** Raw display strings — used for column-width sizing. */
	rawRows: string[][];
	/** Parsed cells — typed values + XLSX number formats. */
	parsedRows: ParsedCell[][];
}

function extractTable(table: HTMLTableElement): ExtractedTable {
	const headers: string[] = [];
	const rawRows: string[][] = [];
	const parsedRows: ParsedCell[][] = [];

	table
		.querySelectorAll("thead th")
		.forEach((th) => headers.push((th.textContent ?? "").trim()));

	table.querySelectorAll("tbody tr").forEach((tr) => {
		const raw: string[] = [];
		const parsed: ParsedCell[] = [];
		tr.querySelectorAll("td").forEach((td) => {
			const text = (td.textContent ?? "").trim();
			raw.push(text);
			parsed.push(parseNumericCell(text));
		});
		rawRows.push(raw);
		parsedRows.push(parsed);
	});

	return { headers, rawRows, parsedRows };
}

// ---------------------------------------------------------------------------
// buildWorkbook  (shared core for message-level & chat-level exports)
// ---------------------------------------------------------------------------

function buildWorkbook(tables: HTMLTableElement[]): XLSX.WorkBook {
	const workbook = XLSX.utils.book_new();

	tables.forEach((table, idx) => {
		const { headers, rawRows, parsedRows } = extractTable(table);

		const aoa: (string | number)[][] = [
			headers,
			...parsedRows.map((row) => row.map((c) => c.value)),
		];

		const worksheet = XLSX.utils.aoa_to_sheet(aoa);

		parsedRows.forEach((row, rIdx) => {
			row.forEach((cell, cIdx) => {
				if (!cell.format) return;
				const addr = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
				if (worksheet[addr]) {
					worksheet[addr].z = cell.format;
				}
			});
		});

		worksheet["!cols"] = headers.map((h, cIdx) => {
			const maxData =
				rawRows.length > 0
					? Math.max(...rawRows.map((r) => (r[cIdx] ?? "").length))
					: 0;
			return { wch: Math.max(h.length, maxData, 10) };
		});

		const range = XLSX.utils.decode_range(worksheet["!ref"] ?? "A1");
		for (let c = range.s.c; c <= range.e.c; c++) {
			const addr = XLSX.utils.encode_cell({ r: 0, c });
			if (!worksheet[addr]) continue;
			worksheet[addr].s = {
				font: { bold: true },
				fill: { fgColor: { rgb: "F0F0F0" } },
			};
		}

		const sheetName = tables.length === 1 ? "Table" : `Table ${idx + 1}`;
		XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
	});

	return workbook;
}

// ---------------------------------------------------------------------------
// exportMessageTablesToExcel
// ---------------------------------------------------------------------------

/**
 * Extract every `<table>` inside *container*, convert to typed XLSX
 * worksheets (one per table), and trigger a browser download.
 *
 * Numeric cells get proper XLSX number formats so formulas / sorting /
 * charting work out-of-the-box in Excel.
 */
export function exportMessageTablesToExcel(
	container: HTMLElement,
	messageId: string,
): void {
	const tables = Array.from(container.querySelectorAll<HTMLTableElement>("table"));
	if (tables.length === 0) return;

	const workbook = buildWorkbook(tables);
	const date = new Date().toISOString().split("T")[0].replace(/-/g, "_");
	XLSX.writeFile(workbook, `Chat_Message_${messageId}_${date}.xlsx`);
}

// ---------------------------------------------------------------------------
// exportParsedTablesToExcel  (for Univer-rendered tables without DOM <table>)
// ---------------------------------------------------------------------------

/**
 * Build an XLSX workbook from pre-parsed table data (columns + rows) and
 * trigger a browser download. Used when tables are rendered via Univer (canvas)
 * instead of DOM `<table>` elements.
 */
export function exportParsedTablesToExcel(
	tables: { columns: { key: string; label: string }[]; rows: Record<string, string>[] }[],
	messageId: string,
): void {
	if (tables.length === 0) return;

	const workbook = XLSX.utils.book_new();

	tables.forEach((table, idx) => {
		const headers = table.columns.map((c) => c.label);
		const rawRows = table.rows.map((row) =>
			table.columns.map((col) => row[col.key] ?? ""),
		);
		const parsedRows = rawRows.map((row) => row.map(parseNumericCell));

		const aoa: (string | number)[][] = [
			headers,
			...parsedRows.map((row) => row.map((c) => c.value)),
		];

		const worksheet = XLSX.utils.aoa_to_sheet(aoa);

		parsedRows.forEach((row, rIdx) => {
			row.forEach((cell, cIdx) => {
				if (!cell.format) return;
				const addr = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
				if (worksheet[addr]) {
					worksheet[addr].z = cell.format;
				}
			});
		});

		worksheet["!cols"] = headers.map((h, cIdx) => {
			let maxData = 0;
			for (const r of rawRows) {
				const len = (r[cIdx] ?? "").length;
				if (len > maxData) maxData = len;
			}
			return { wch: Math.max(h.length, maxData, 10) };
		});

		const sheetName = tables.length === 1 ? "Table" : `Table ${idx + 1}`;
		XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
	});

	const date = new Date().toISOString().split("T")[0].replace(/-/g, "_");
	XLSX.writeFile(workbook, `Chat_Message_${messageId}_${date}.xlsx`);
}

// ---------------------------------------------------------------------------
// GFM pipe-table parsing from raw markdown text
// ---------------------------------------------------------------------------

/**
 * Extract GFM pipe tables from markdown text, returning structured column/row
 * data.  Fenced code blocks are stripped before scanning.
 */
export function parseTablesFromMarkdown(text: string): ParsedTable[] {
	const tables: ParsedTable[] = [];
	const textWithoutCode = text.replace(/```[\s\S]*?```/g, "");
	const tableRegex = /(?:^|\n)(\|.+\|)\n(\|[-: |]+\|)\n((?:\|.+\|\n?)*)/g;
	let match;
	while ((match = tableRegex.exec(textWithoutCode)) !== null) {
		const headerLine = match[1];
		const dataBlock = match[3].trim();
		const parsePipeLine = (line: string): string[] => {
			const parts = line.split("|");
			if (parts.length > 0 && parts[0].trim() === "") parts.shift();
			if (parts.length > 0 && parts[parts.length - 1].trim() === "") parts.pop();
			return parts.map((s) => s.trim());
		};
		const headers = parsePipeLine(headerLine);
		const columns = headers.map((h, i) => ({ key: `col_${i}`, label: h }));
		const rows = dataBlock
			.split("\n")
			.filter((line) => line.includes("|"))
			.map((line) => {
				const cells = parsePipeLine(line);
				const row: Record<string, string> = {};
				columns.forEach((col, i) => { row[col.key] = cells[i] ?? ""; });
				return row;
			});
		if (columns.length > 0 && rows.length > 0) {
			tables.push({ columns, rows });
		}
	}
	return tables;
}

/**
 * Extract all GFM tables from assistant messages.
 */
export function parseTablesFromMessages(messages: UIMessage[]): ParsedTable[] {
	const tables: ParsedTable[] = [];
	for (const msg of messages) {
		if (msg.role !== "assistant") continue;
		for (const block of msg.content_blocks) {
			if (block.type !== BlockType.TEXT) continue;
			tables.push(...parseTablesFromMarkdown(block.text));
		}
	}
	return tables;
}

// ---------------------------------------------------------------------------
// Chat-level helpers (scoped to [data-export-answer] only)
// ---------------------------------------------------------------------------

/**
 * Collect every chat-rendered `<table>` that should appear in the export.
 *
 * Scoped to `[data-export-answer]` (the assistant answer surface) and
 * filtered to drop tables inside any `[data-export-exclude]` ancestor.
 * The exclude attribute is set on download cards that already have a
 * dedicated artifact in the export — notably the
 * `ModifiedWorkbookBlockView` preview, whose `<table>` would otherwise
 * be re-extracted into a duplicate xlsx alongside the real workbook
 * artifact.
 */
function collectChatExportTables(container: HTMLElement): HTMLTableElement[] {
	const answerDivs = container.querySelectorAll<HTMLElement>(
		"[data-export-answer]",
	);
	const tables: HTMLTableElement[] = [];
	answerDivs.forEach((div) => {
		div.querySelectorAll<HTMLTableElement>("table").forEach((t) => {
			if (t.closest("[data-export-exclude]")) return;
			tables.push(t);
		});
	});
	return tables;
}

/**
 * Returns true when the chat contains exportable tables — either DOM `<table>`
 * elements inside `[data-export-answer]` (excluding those under
 * `[data-export-exclude]`), or GFM pipe tables parsed from assistant message
 * text (Univer mode).
 */
export function chatHasTables(
	container: HTMLElement | null,
	messages?: UIMessage[],
): boolean {
	if (container && collectChatExportTables(container).length > 0) {
		return true;
	}
	if (messages && parseTablesFromMessages(messages).length > 0) {
		return true;
	}
	return false;
}

/**
 * Collect tables from the chat and trigger a single XLSX download.
 *
 * First tries DOM `<table>` elements (streaming / fallback mode).  If none
 * are found, falls back to parsing GFM pipe tables from the raw message text
 * (Univer mode where tables render as canvas, not DOM elements).
 *
 * @throws {Error} if no tables are found — caller should handle with a toast.
 */
export function exportChatTablesToExcel(
	container: HTMLElement,
	chatId: string,
	messages?: UIMessage[],
): void {
	// Try DOM tables first (excludes any inside [data-export-exclude]).
	const allTables = collectChatExportTables(container);

	if (allTables.length > 0) {
		const workbook = buildWorkbook(allTables);
		const date = new Date().toISOString().split("T")[0].replace(/-/g, "_");
		XLSX.writeFile(workbook, `Chat_${chatId}_${date}.xlsx`);
		return;
	}

	// Fall back to parsed markdown tables (Univer mode)
	const parsed = messages ? parseTablesFromMessages(messages) : [];
	if (parsed.length === 0) {
		throw new Error("No tables found in chat");
	}

	exportParsedTablesToExcel(parsed, chatId);
}

// ---------------------------------------------------------------------------
// Bundled "Export as Excel" — zip with one xlsx per table-shaped output
// (chat-rendered table or modified_workbook artifact). No merging.
// ---------------------------------------------------------------------------

/**
 * True when any assistant message carries a `modified_workbook` block.
 *
 * Used to decide whether the export bundle has a server-side component
 * regardless of whether the chat also has DOM/markdown tables.  Includes
 * `hidden` blocks because the durable artifact is preserved for export
 * even when the inline card is suppressed.
 */
export function chatHasWorkbookArtifacts(messages: UIMessage[]): boolean {
	for (const msg of messages) {
		if (msg.role !== "assistant") continue;
		for (const block of msg.content_blocks) {
			if (block.type === BlockType.MODIFIED_WORKBOOK) return true;
		}
	}
	return false;
}

/**
 * True when the chat has anything to export — DOM tables, markdown tables,
 * or modified_workbook artifacts.  Drives the dropdown's enable state.
 */
export function chatHasExportableContent(
	container: HTMLElement | null,
	messages: UIMessage[],
): boolean {
	return chatHasTables(container, messages) || chatHasWorkbookArtifacts(messages);
}

const XLSX_BLOB_TYPE =
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Wrap a single-sheet workbook (one extracted table) as an xlsx Blob.
 *
 * Used by {@link buildChatTableBlobs} to give every chat table its own
 * file in the export zip. Mirrors the per-sheet styling that
 * {@link buildWorkbook} applies, so a single-table xlsx looks the same
 * as a tab inside the old merged workbook would have looked.
 */
function singleDomTableToBlob(table: HTMLTableElement): Blob {
	const wb = buildWorkbook([table]);
	const bytes = XLSX.write(wb, { type: "array", bookType: "xlsx" });
	return new Blob([bytes], { type: XLSX_BLOB_TYPE });
}

function singleParsedTableToBlob(table: ParsedTable): Blob {
	const headers = table.columns.map((c) => c.label);
	const rawRows = table.rows.map((row) =>
		table.columns.map((col) => row[col.key] ?? ""),
	);
	const parsedRows = rawRows.map((row) => row.map(parseNumericCell));
	const aoa: (string | number)[][] = [
		headers,
		...parsedRows.map((row) => row.map((c) => c.value)),
	];
	const ws = XLSX.utils.aoa_to_sheet(aoa);
	parsedRows.forEach((row, rIdx) => {
		row.forEach((cell, cIdx) => {
			if (!cell.format) return;
			const addr = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
			if (ws[addr]) ws[addr].z = cell.format;
		});
	});
	ws["!cols"] = headers.map((h, cIdx) => {
		let maxData = 0;
		for (const r of rawRows) {
			const len = (r[cIdx] ?? "").length;
			if (len > maxData) maxData = len;
		}
		return { wch: Math.max(h.length, maxData, 10) };
	});
	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, "Table");
	const bytes = XLSX.write(wb, { type: "array", bookType: "xlsx" });
	return new Blob([bytes], { type: XLSX_BLOB_TYPE });
}

/**
 * Build one xlsx Blob per chat-rendered table — DOM `<table>` elements
 * scoped to `[data-export-answer]` first (excluding any inside
 * `[data-export-exclude]`, e.g. the modified-workbook preview card),
 * falling back to GFM pipe-tables parsed from assistant message text
 * (Univer mode where tables render as canvas instead of DOM).
 *
 * Returns an array; empty when the chat has no chat-rendered tables.
 * Workbook artifacts are bundled separately by
 * {@link exportChatBundleZip} via the backend listing endpoint.
 */
function buildChatTableBlobs(
	container: HTMLElement | null,
	messages: UIMessage[],
): Blob[] {
	if (container) {
		const allTables = collectChatExportTables(container);
		if (allTables.length > 0) {
			return allTables.map(singleDomTableToBlob);
		}
	}

	const parsed = parseTablesFromMessages(messages);
	return parsed.map(singleParsedTableToBlob);
}

/**
 * Compact ISO timestamp suitable for a filename: "20260430T101530Z".
 */
function compactIsoStamp(epochMs: number): string {
	const iso = new Date(epochMs).toISOString();
	// 2026-04-30T10:15:30.123Z → 20260430T101530Z
	return iso.replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

/**
 * Sanitize a filename component so it survives every common filesystem and
 * the zip directory.  Keeps alphanumerics, dot, dash, underscore.
 */
function sanitizeFilename(name: string): string {
	return name.replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 120) || "file";
}

/**
 * Fetch the bytes for one workbook artifact through the backend proxy.
 *
 * Going through the API instead of the GCS signed URL avoids needing
 * browser-origin CORS configured on every per-tenant bucket.
 */
async function fetchWorkbookArtifactBlob(
	chatId: string,
	item: WorkbookArtifactItem,
): Promise<Blob | null> {
	try {
		return await chatApi.downloadWorkbookArtifact(chatId, item.block_id);
	} catch (err) {
		console.warn(
			`Workbook artifact ${item.block_id} download failed:`,
			err,
		);
		return null;
	}
}

interface ExportBundleResult {
	totalFiles: number;
	skippedWorkbooks: number;
}

/**
 * Build and download a single zip containing every table-shaped output
 * in the chat — extraction tables, web-search tables, model-emitted
 * markdown tables, and every `modified_workbook` xlsx, each as its own
 * file. No merging, no flattening: one table = one xlsx.
 *
 * Filenames are `{NN}_{compactIso}.xlsx` for every entry — chat tables
 * and workbook artifacts alike. ``NN`` is the export ordinal; the
 * timestamp comes from the parent message for workbook artifacts and
 * from the export moment for chat-rendered tables (we don't track
 * per-table timestamps for those). The original workbook filenames are
 * intentionally not preserved inside the zip so the layout stays flat
 * and uniform.
 *
 * Uses `STORE` compression — xlsx is already zipped, re-compressing
 * buys ~nothing and slows the export.
 *
 * @throws {Error} when the chat has no tables AND no workbook artifacts.
 */
export async function exportChatBundleZip(
	container: HTMLElement | null,
	chatId: string,
	chatTitle: string,
	messages: UIMessage[],
): Promise<ExportBundleResult> {
	let artifacts: WorkbookArtifactItem[] = [];
	if (chatHasWorkbookArtifacts(messages)) {
		const response = await chatApi.listWorkbookArtifacts(chatId);
		artifacts = response.items;
	}

	const chatTableBlobs = buildChatTableBlobs(container, messages);

	if (artifacts.length === 0 && chatTableBlobs.length === 0) {
		throw new Error("No tables or workbook artifacts found in this chat");
	}

	type ZipEntry = { blob: Blob; ts: number };
	const entries: ZipEntry[] = [];

	// Chat tables come first; we don't know their per-message timestamp
	// from the DOM/markdown extractor, so they share the export moment
	// and rely on ordinal for ordering within their block.
	const exportTs = Date.now();
	for (const blob of chatTableBlobs) {
		entries.push({ blob, ts: exportTs });
	}

	let skippedWorkbooks = 0;
	for (const item of artifacts) {
		const blob = await fetchWorkbookArtifactBlob(chatId, item);
		if (!blob) {
			skippedWorkbooks++;
			continue;
		}
		entries.push({ blob, ts: item.message_created_at_ms });
	}

	if (entries.length === 0) {
		throw new Error("Could not download workbook artifacts.");
	}

	const zip = new JSZip();
	const zipOptions: JSZip.JSZipFileOptions = { compression: "STORE" };
	entries.forEach((entry, idx) => {
		const ordinal = String(idx + 1).padStart(2, "0");
		const stamp = compactIsoStamp(entry.ts);
		zip.file(`${ordinal}_${stamp}.xlsx`, entry.blob, zipOptions);
	});

	const zipBlob = await zip.generateAsync({
		type: "blob",
		compression: "STORE",
	});

	const date = new Date().toISOString().split("T")[0].replace(/-/g, "_");
	const safeTitle = sanitizeFilename(chatTitle || `Chat_${chatId}`);
	const url = URL.createObjectURL(zipBlob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `${safeTitle}_${date}.zip`;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);

	return {
		totalFiles: entries.length,
		skippedWorkbooks,
	};
}
